// SheetPicker.tsx — "which Google Sheet" dropdown.
// Loads only the 5 most recently synced sheets, and searches the server for
// the rest, so a long sheet list is never fetched at once.
// Used by the Leads filters and the Spend Report.

import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Search, X } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { SheetOption, fetchSheetOptions } from './leadOrigin';

interface SheetPickerProps {
  /** Spreadsheet ID, or '' for all sheets. */
  value: string;
  onChange: (sheetId: string, label: string) => void;
  /** Text for the "everything" choice. */
  allLabel?: string;
  /** No own border (sits inside a bordered group, like the Leads filters). */
  bare?: boolean;
  maxWidth?: number;
}

const SEARCH_DELAY_MS = 300;

const SheetPicker: React.FC<SheetPickerProps> = ({ value, onChange, allLabel = 'All Sheets', bare, maxWidth = 200 }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<SheetOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [label, setLabel] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const reqId = useRef(0);

  // Name of the selected sheet (e.g. when it came in from a link).
  useEffect(() => {
    if (!value) {
      setLabel('');
      return;
    }
    const known = options.find((o) => o.value === value);
    if (known) {
      setLabel(known.label);
      return;
    }
    let live = true;
    fetchSheetOptions('', value)
      .then((list) => live && setLabel(list.find((o) => o.value === value)?.label ?? 'Selected sheet'))
      .catch(() => live && setLabel('Selected sheet'));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Recent 5 when the box opens / the search is cleared; server search otherwise.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    const id = ++reqId.current;
    const run = () => {
      setLoading(true);
      setFailed(false);
      fetchSheetOptions(q, q ? '' : value)
        .then((list) => id === reqId.current && setOptions(list))
        .catch(() => id === reqId.current && setFailed(true))
        .finally(() => id === reqId.current && setLoading(false));
    };
    if (!q) {
      run();
      return;
    }
    const t = setTimeout(run, SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [open, query, value]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (v: string, l: string) => {
    setLabel(l);
    setOpen(false);
    setQuery('');
    onChange(v, l);
  };

  const shown = value ? label || 'Selected sheet' : allLabel;

  const item = (v: string, l: string, count?: number) => {
    const on = v === value;
    return (
      <button
        key={v || '__all'}
        type="button"
        role="option"
        aria-selected={on}
        onClick={() => pick(v, l)}
        className="shp-item"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          width: '100%',
          padding: '7px 9px',
          border: 'none',
          borderRadius: '6.5px',
          background: on ? C.limeDim : 'transparent',
          color: on ? '#3d4d00' : '#374151',
          fontSize: '0.71rem',
          fontWeight: on ? 700 : 500,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span style={{ width: 12, display: 'flex', flexShrink: 0 }}>{on && <Check size={12} strokeWidth={3} />}</span>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l}</span>
        {count !== undefined && <span style={{ fontSize: '0.62rem', color: C.muted, fontWeight: 600 }}>{count}</span>}
      </button>
    );
  };

  return (
    <div ref={box} style={{ position: 'relative', display: 'inline-flex' }}>
      <style>{`.shp-item:hover { background: ${G.shellBg} !important; } .shp-spin { animation: shpSpin 0.9s linear infinite; } @keyframes shpSpin { to { transform: rotate(360deg); } }`}</style>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Sheet"
        title="Every lead ever pushed from this Google Sheet"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          height: bare ? '33px' : '31px',
          maxWidth,
          minWidth: '120px',
          padding: '0 9px',
          background: 'white',
          border: bare ? 'none' : `1px solid ${G.line}`,
          borderRadius: bare ? '7.4px 0 0 7.4px' : '7.4px',
          color: value ? C.teal : '#374151',
          fontSize: '0.71rem',
          fontWeight: value ? 700 : 500,
          cursor: 'pointer',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>{shown}</span>
        <svg width="9" height="5" viewBox="0 0 9 5" style={{ flexShrink: 0 }} aria-hidden>
          <path d="M0 0l4.5 5L9 0z" fill="rgba(17,85,80,0.45)" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 5px)',
            left: 0,
            zIndex: 50,
            width: '260px',
            maxWidth: '86vw',
            background: 'white',
            border: `1px solid ${G.line}`,
            borderRadius: '10px',
            boxShadow: '0 14px 32px -10px rgba(17,85,80,0.28)',
            padding: '6px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              height: '31px',
              padding: '0 8px',
              marginBottom: '4px',
              border: `1px solid ${G.line}`,
              borderRadius: '7px',
              background: G.shellBg,
            }}
          >
            <Search size={11} color={C.muted} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search sheets…"
              style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.7rem', color: C.teal }}
            />
            {loading ? (
              <Loader2 size={11} color={C.muted} className="shp-spin" />
            ) : (
              query && (
                <button type="button" onClick={() => setQuery('')} title="Clear" style={{ border: 'none', background: 'none', padding: 0, display: 'flex', cursor: 'pointer', color: C.muted }}>
                  <X size={11} />
                </button>
              )
            )}
          </div>

          <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
            {!query.trim() && item('', allLabel)}
            {options.map((o) => item(o.value, o.label, o.count))}
            {!loading && !failed && query.trim() && options.length === 0 && (
              <div style={{ padding: '10px 9px', fontSize: '0.68rem', color: C.muted }}>No sheet matches “{query.trim()}”.</div>
            )}
            {failed && <div style={{ padding: '10px 9px', fontSize: '0.68rem', color: '#c62828' }}>Couldn’t load sheets. Try again.</div>}
          </div>

          {!query.trim() && (
            <div style={{ padding: '6px 9px 2px', borderTop: `1px solid ${G.softer}`, marginTop: '4px', fontSize: '0.6rem', color: C.muted }}>
              5 most recently synced · search to find others
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SheetPicker;
